import React, { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { CreditCard, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StripeSubscriptionPayment = ({ subscription, slug, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
  const { toast } = useToast();

  useEffect(() => { initializePayment(); }, [slug]);

  const initializePayment = async () => {
    setInitializing(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/subscription/${slug}/create-setup-intent`);
      const { client_secret, publishable_key } = response.data;
      setSetupIntentClientSecret(client_secret);

      if (window.Stripe) {
        const stripeInstance = window.Stripe(publishable_key);
        setStripe(stripeInstance);
        const elementsInstance = stripeInstance.elements({
          clientSecret: client_secret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#e6007e', colorBackground: '#ffffff',
              colorText: '#1a1a1a', colorDanger: '#dc2626',
              fontFamily: 'system-ui, sans-serif', borderRadius: '8px'
            }
          }
        });
        setElements(elementsInstance);

        setTimeout(() => {
          const paymentElement = elementsInstance.create('payment', { layout: 'tabs' });
          const container = document.getElementById('subscription-payment-element');
          if (container) {
            container.innerHTML = '';
            paymentElement.mount('#subscription-payment-element');
            paymentElement.on('change', (event) => {
              setCardComplete(event.complete);
              setError(event.error ? event.error.message : null);
            });
          }
        }, 100);
      } else {
        setError('Stripe is not loaded. Please refresh the page.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to initialize payment.';
      if (errorMessage.includes('does not have access to account') || errorMessage.includes('account does not exist')) {
        setError('The business owner needs to complete their Stripe setup.');
      } else if (errorMessage.includes('has not connected Stripe')) {
        setError('The business owner has not connected their payment account yet.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setInitializing(false);
    }
  };

  const handlePayment = async () => {
    if (!stripe || !elements || !setupIntentClientSecret) {
      toast({ title: "Error", description: "Payment not initialized. Please refresh.", variant: "destructive" });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements, confirmParams: { return_url: window.location.href }, redirect: 'if_required'
      });
      if (confirmError) throw new Error(confirmError.message);

      if (paymentIntent?.status === 'succeeded') {
        const confirmResponse = await axios.post(`${API}/subscription/${slug}/confirm-payment`, {
          payment_intent_id: paymentIntent.id
        });
        if (confirmResponse.data.success) {
          toast({ title: "Success!", description: "Your subscription is now active." });
          onSuccess?.();
        } else {
          throw new Error(confirmResponse.data.message || 'Failed to activate subscription');
        }
      } else if (paymentIntent?.status === 'processing') {
        toast({ title: "Payment Processing", description: "You'll receive a confirmation shortly." });
        onSuccess?.();
      } else {
        throw new Error(`Payment failed with status: ${paymentIntent?.status || 'unknown'}`);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || err.message || "Payment failed.";
      setError(errorDetail);
      toast({ title: "Payment Error", description: errorDetail, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e6007e] mx-auto mb-3"></div>
          <p className="text-sm text-gray-600 font-body">Initializing secure payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-6 h-6 text-gray-400" />
          <span className="font-semibold text-gray-700 font-heading">Payment Details</span>
        </div>
        <div id="subscription-payment-element" className="min-h-[150px]"></div>
        <p className="text-xs text-gray-500 mt-3 font-body">Your card will be charged &pound;{subscription.amount?.toFixed(2)} monthly.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600 font-body">{error}</p>
        </div>
      )}

      <Button onClick={handlePayment} disabled={loading || !cardComplete} className="w-full bg-[#e6007e] hover:bg-[#c90068] text-white font-body disabled:opacity-50 disabled:cursor-not-allowed" data-testid="setup-payment-btn">
        {loading ? 'Processing...' : `Subscribe - \u00A3${subscription.amount?.toFixed(2)}/month`}
      </Button>

      <p className="text-xs text-center text-gray-500 font-body">
        <Shield className="w-3 h-3 inline mr-1" />Payments are secured with bank-level encryption
      </p>
    </div>
  );
};

export default StripeSubscriptionPayment;
